import React from 'react';
import { X, Award, Edit3, Trash2, GitBranch, CheckCircle2, Clock, XCircle, BookOpen, Archive, Printer } from 'lucide-react';
import type { Book } from '../../../../shared/types/index.js';

import { canEditBookMetadata, canDeleteBook } from '../book-list/bookListUtils.js';

interface BookDetailHeaderProps {
  book: Book;
  user: any;
  canEdit: boolean;
  onClose: () => void;
  onOpenEditionTree: () => void;
  onOpenCertificateModal: () => void;
  onOpenPrintLabel?: () => void;
  onEditRequested?: (book: Book) => void;
  onDeleteRequested?: (book: Book) => void;
}

export const BookDetailHeader: React.FC<BookDetailHeaderProps> = ({
  book,
  user,
  canEdit,
  onClose,
  onOpenEditionTree,
  onOpenCertificateModal,
  onOpenPrintLabel,
  onEditRequested,
  onDeleteRequested,
}) => {
  const statusBadges: Record<string, { label: string; bg: string; text: string; icon: any }> = {
    disetujui: { label: 'Disetujui / Aktif Resmi', bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', icon: CheckCircle2 },
    diajukan: { label: 'Diajukan (Menunggu Admin)', bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', icon: Clock },
    menunggu_penerbit: { label: 'Menunggu Kurasi Penerbit', bg: 'bg-purple-50 border-purple-200', text: 'text-purple-700', icon: Clock },
    ditolak: { label: 'Pengajuan Ditolak', bg: 'bg-rose-50 border-rose-200', text: 'text-rose-700', icon: XCircle },
    draft: { label: 'Draft Naskah', bg: 'bg-slate-100 border-slate-200', text: 'text-slate-700', icon: BookOpen },
    diarsipkan: { label: 'Diarsipkan', bg: 'bg-stone-100 border-stone-200', text: 'text-stone-600', icon: Archive },
  };

  const currentStatusInfo = statusBadges[book.status] || statusBadges.draft;
  const StatusIcon = currentStatusInfo.icon;
  const isApproved = book.status === 'disetujui';
  const canModify = canEditBookMetadata(book, user);

  return (
    <div className="px-3.5 py-3 sm:px-6 sm:py-4 border-b border-slate-200/80 bg-slate-50/95 sticky top-0 z-20 backdrop-blur-md shadow-xs relative">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3 pr-10 sm:pr-12">
        {/* Top Section: Badges */}
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${currentStatusInfo.bg} ${currentStatusInfo.text} shrink-0 max-w-[200px] xs:max-w-none truncate`}
          >
            <StatusIcon className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{currentStatusInfo.label}</span>
          </span>
          <span className="font-mono text-xs text-slate-600 font-semibold bg-white/90 px-2 py-0.5 rounded-md border border-slate-200/80 truncate shrink-0">
            {book.uibn}
          </span>
        </div>

        {/* Action Buttons Row: Icon-only buttons with explicit tooltips */}
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            id="btn-modal-open-edition-tree"
            type="button"
            onClick={onOpenEditionTree}
            className="w-8 h-8 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 flex items-center justify-center transition cursor-pointer shadow-2xs shrink-0"
            title="Silsilah Edisi Buku (Edition Tree)"
            aria-label="Silsilah Edisi Buku"
          >
            <GitBranch className="w-4 h-4 text-indigo-600 shrink-0" />
          </button>

          {isApproved && (
            <button
              id="btn-modal-open-certificate"
              type="button"
              onClick={onOpenCertificateModal}
              className="w-8 h-8 rounded-lg bg-amber-400 hover:bg-amber-500 text-slate-950 border border-amber-500/50 flex items-center justify-center transition shadow-xs cursor-pointer shrink-0"
              title="Lihat E-Sertifikat Digital PDF"
              aria-label="E-Sertifikat Digital PDF"
            >
              <Award className="w-4 h-4 shrink-0" />
            </button>
          )}

          {isApproved && canEdit && onOpenPrintLabel && (
            <button
              id="btn-modal-open-print-label"
              type="button"
              onClick={onOpenPrintLabel}
              className="w-8 h-8 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center transition shadow-xs cursor-pointer shrink-0"
              title="Cetak Label & Barcode UIBN (Printer-Friendly)"
              aria-label="Cetak Label & Barcode UIBN"
            >
              <Printer className="w-4 h-4 shrink-0" />
            </button>
          )}

          {canEdit && canModify && onEditRequested && (
            <button
              type="button"
              onClick={() => {
                onClose();
                onEditRequested(book);
              }}
              className="w-8 h-8 rounded-lg text-indigo-700 bg-indigo-50/80 hover:bg-indigo-100 border border-indigo-200/70 flex items-center justify-center transition cursor-pointer shrink-0"
              title="Ubah Data Buku"
              aria-label="Ubah Data Buku"
            >
              <Edit3 className="w-4 h-4 shrink-0" />
            </button>
          )}

          {canDeleteBook(book, user) && onDeleteRequested && (
            <button
              type="button"
              onClick={() => {
                onClose();
                onDeleteRequested(book);
              }}
              className="w-8 h-8 rounded-lg text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 flex items-center justify-center transition cursor-pointer shrink-0"
              title="Hapus Draft UIBN Ini"
              aria-label="Hapus Draft UIBN Ini"
            >
              <Trash2 className="w-4 h-4 shrink-0 text-rose-600" />
            </button>
          )}
        </div>
      </div>

      {/* Close button - Positioned absolutely at top-right */}
      <button
        id="btn-close-book-detail-modal"
        type="button"
        onClick={onClose}
        aria-label="Tutup detail buku"
        className="absolute top-3.5 right-3.5 sm:top-4.5 sm:right-6 w-8 h-8 rounded-lg bg-slate-200/70 hover:bg-rose-100 text-slate-700 hover:text-rose-700 border border-slate-300 hover:border-rose-300 flex items-center justify-center transition cursor-pointer shrink-0 shadow-2xs z-30"
        title="Tutup (Esc)"
      >
        <X className="w-4.5 h-4.5 stroke-[2.2]" />
      </button>
    </div>
  );
};
